import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchServerHealth, dismissServerIncident } from '../../../services/adminApi';
import '../catalog/catalog.css';
import '../books/people.css';
import './ops.css';

// État du serveur (mémoire, processeur, disque, rendus PDF, file des rendus, sauvegarde), actualisé toutes les 15 secondes.
// Un seuil visuel, pas une alerte : de quoi repérer en balayant ce qui approche de ses limites. Les mesures viennent de
// backend/services/events/serverHealth.js ; une mesure impossible s'affiche « — », jamais une valeur inventée.

const duree = (secondes, t) => {
  if (!Number.isFinite(secondes)) return '—';
  const j = Math.floor(secondes / 86400);
  const h = Math.floor((secondes % 86400) / 3600);
  const m = Math.floor((secondes % 3600) / 60);
  if (j > 0) return t('ops.health.durationDays', { d: j, h });
  if (h > 0) return t('ops.health.durationHours', { h, m });
  return t('ops.health.durationMinutes', { m });
};
const ton = (pourcent) => (!Number.isFinite(pourcent) ? '' : pourcent >= 90 ? 'is-critique' : pourcent >= 70 ? 'is-tendu' : '');

function Mesure({ label, value, detail, percent }) {
  return (
    <div className={`adm-kpi adm-meter ${ton(percent)}`}>
      <span>{label}</span>
      <b>{value}</b>
      {detail && <small>{detail}</small>}
      {Number.isFinite(percent) && (
        <div className="adm-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(100, percent)} aria-label={label}>
          <i style={{ width: `${Math.min(100, percent)}%` }} />
        </div>
      )}
    </div>
  );
}

export default function ServerHealthSection() {
  const { t, i18n } = useTranslation('admin');
  const [etat, setEtat] = useState(null);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(async () => {
    try { setEtat(await fetchServerHealth()); setErreur(''); } catch (err) { setErreur(err?.message || t('ops.health.failed')); }
  }, [t]);
  useEffect(() => {
    charger();
    // Rafraîchi seul : une page de supervision qu'il faut recharger à la main ne sert à rien pendant qu'on regarde un rendu tourner.
    const minuteur = setInterval(() => { if (document.visibilityState !== 'hidden') charger(); }, 15000);
    return () => clearInterval(minuteur);
  }, [charger]);

  if (erreur && !etat) return <p className="adm-error" role="alert">{erreur}</p>;
  if (!etat) return <p className="adm-state" role="status">{t('ops.health.loading')}</p>;

  const memoire = etat.memoire;
  const memoirePourcent = memoire.machineTotaleMo ? Math.round((memoire.machineUtiliseeMo / memoire.machineTotaleMo) * 100) : null;
  const incident = etat.dernierIncident;
  const quand = (iso) => (iso ? new Date(iso).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' }) : '—');

  return (
    <section>
      {incident && (
        <div className="adm-alert" role="status">
          <div>
            <b>{t('ops.health.incident', { date: quand(incident.quand) })}</b>
            <p>
              {incident.action}
              {incident.memoire ? ` · ${t('ops.health.memory')} ${incident.memoire}` : ''}
              {incident.charge ? ` · ${t('ops.health.load')} ${incident.charge}` : ''}
              {incident.navigateursDeRendu ? ` · ${t('ops.health.renderBrowsers', { count: incident.navigateursDeRendu })}` : ''}
              {' — '}{t('ops.health.incidentNote')}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={async () => { try { await dismissServerIncident(); await charger(); } catch (_e) { /* le bandeau reste : rien de grave */ } }}
          >
            {t('ops.incidents.delete')}
          </button>
        </div>
      )}
      <div className="adm-kpis">
        <Mesure label={t('ops.health.machineMemory')} value={`${memoire.machineUtiliseeMo} / ${memoire.machineTotaleMo} Mo`} detail={t('ops.health.free', { value: memoire.machineLibreMo })} percent={memoirePourcent} />
        <Mesure label={t('ops.health.appMemory')} value={`${memoire.processusMo} Mo`} detail={t('ops.health.renderNeeds')} />
        <Mesure label={t('ops.health.cpu')} value={`${etat.processeur.pourcent1min} %`} detail={t('ops.health.cpuLoad', { load: etat.processeur.charge1min, cores: etat.processeur.coeurs })} percent={etat.processeur.pourcent1min} />
        {etat.disque
          ? <Mesure label={t('ops.health.disk')} value={`${Math.round(etat.disque.utiliseMo / 1024)} / ${Math.round(etat.disque.totalMo / 1024)} Go`} detail={t('ops.health.diskUsed', { percent: etat.disque.pourcent })} percent={etat.disque.pourcent} />
          : <Mesure label={t('ops.health.disk')} value="—" detail={t('ops.health.diskUnknown')} />}
        <Mesure
          label={t('ops.health.renders')} value={etat.rendusEnCours === null ? '—' : String(etat.rendusEnCours)}
          detail={etat.navigateursDeRendu && etat.navigateursDeRendu.processus > 0 ? t('ops.health.browsers', { count: etat.navigateursDeRendu.processus, mb: etat.navigateursDeRendu.memoireMo }) : t('ops.health.noBrowser')}
        />
        {/* Les rendus passent un par un : deux Chrome en même temps rendaient le serveur injoignable. S'il ne redescend jamais, un rendu est bloqué. */}
        <Mesure
          label={t('ops.health.queue')} value={etat.rendusEnFile === null || etat.rendusEnFile === undefined ? '—' : String(etat.rendusEnFile)}
          detail={etat.rendusEnFile > 1 ? t('ops.health.waiting', { count: etat.rendusEnFile - 1 }) : t('ops.health.oneAtATime')}
        />
        <Mesure label={t('ops.health.uptime')} value={duree(etat.demarreDepuisSecondes, t)} detail={t('ops.health.machineUptime', { value: duree(etat.machineDemarreeDepuisSecondes, t) })} />
        <Mesure
          label={t('ops.health.backup')} value={etat.derniereSauvegarde ? quand(etat.derniereSauvegarde.quand) : t('ops.health.none')}
          detail={etat.derniereSauvegarde ? t('ops.health.backupDetail', { size: etat.derniereSauvegarde.tailleMo, count: etat.derniereSauvegarde.nombre }) : t('ops.health.notProtected')}
        />
      </div>
      <p className="adm-note">{t('ops.health.measured', { time: new Date(etat.mesureLe).toLocaleTimeString(i18n.language), env: etat.environnement, version: etat.version || '?' })}</p>
    </section>
  );
}
